import { generateClient } from 'aws-amplify/api';
import {
	campaignsByTenant,
	listContactFieldSuppressionLists,
	listPaymentProfiles,
	listSuppressions,
	manualProfileByTenant,
	profileByTenant,
	segmentByTenant,
} from '../graphql/queries';
import { fetchAllPages } from './graphqlPaging';

/**
 * All check*Usage functions return an object of the shape:
 *   { inUse: false }
 * or
 *   { inUse: true, message: string, usedBy: Array<{ name, type, status? }> }
 *
 * `usedBy` lists the specific entities that reference the item being deleted so
 * the UI can show the user exactly what is blocking the delete.
 */

export async function checkSegmentUsage(segmentId, tenantId) {
	// Check across ALL campaigns regardless of status. Previously this only
	// looked at Active/Paused campaigns, which let users delete segments that
	// were still referenced by Pending campaigns (including clones of
	// completed campaigns, which are created in a Pending state).
	const tenantCampaigns = await getAllTenantCampaigns(tenantId);

	// Collect every campaign that references the segment so the caller can
	// show the user exactly what is blocking the delete.
	const usedBy = tenantCampaigns
		.filter((campaign) => {
			if (!campaign.segments) {
				return false;
			}
			try {
				return JSON.parse(campaign.segments).some(
					(segment) => segment.segmentId === segmentId,
				);
			} catch (error) {
				console.error(
					`Failed to parse segments for campaign ${campaign.id}`,
					error,
				);
				return false;
			}
		})
		.map((campaign) => ({
			name: campaign.name,
			type: 'Campaign',
			status: campaign.status,
		}));

	if (usedBy.length > 0) {
		return {
			inUse: true,
			message:
				'This segment is currently in use by one or more campaigns and cannot be deleted.',
			usedBy,
		};
	}

	const manualProfiles = await tenantGraphqlRequest(
		manualProfileByTenant,
		tenantId,
	);
	const manualProfilesUsingSegment = manualProfiles.manualProfileByTenant.items
		.filter((profile) => profile.segment === segmentId)
		.map((profile) => ({ name: profile.name, type: 'Manual Dialing Profile' }));

	if (manualProfilesUsingSegment.length > 0) {
		return {
			inUse: true,
			message:
				'This segment is currently in use by a manual dialing profile and cannot be deleted.',
			usedBy: manualProfilesUsingSegment,
		};
	}

	return { inUse: false };
}

export async function checkCommunicationProfileUsage(profileId, tenantId) {
	// Check across ALL campaigns regardless of status so Pending campaigns
	// (including clones) that reference the profile are not missed.
	const tenantCampaigns = await getAllTenantCampaigns(tenantId);

	// Check if any of the tenant's campaigns are using the communication profile
	const campaignsUsingProfile = tenantCampaigns
		.filter((campaign) => campaign.campaignProfileId === profileId)
		.map((campaign) => ({
			name: campaign.name,
			type: 'Campaign',
			status: campaign.status,
		}));

	if (campaignsUsingProfile.length > 0) {
		return {
			inUse: true,
			message:
				'This communication profile is currently in use by one or more campaigns and cannot be deleted.',
			usedBy: campaignsUsingProfile,
		};
	}

	const manualProfiles = await tenantGraphqlRequest(
		manualProfileByTenant,
		tenantId,
	);
	const manualProfilesUsingProfile = manualProfiles.manualProfileByTenant.items
		.filter((profile) => profile.dialingProfile === profileId)
		.map((profile) => ({ name: profile.name, type: 'Manual Dialing Profile' }));

	if (manualProfilesUsingProfile.length > 0) {
		return {
			inUse: true,
			message:
				'This communication profile is currently in use by a manual dialing profile and cannot be deleted.',
			usedBy: manualProfilesUsingProfile,
		};
	}

	return { inUse: false };
}

export async function checkMessageTemplateUsage(templateId, tenantId) {
	const digitalProfiles = await tenantGraphqlRequest(
		listPaymentProfiles,
		tenantId,
	);

	// Check if any of the tenant's digital channels are using the message template
	const usedBy = [];
	for (const profile of digitalProfiles.listPaymentProfiles.items) {
		try {
			const channels = JSON.parse(profile.channels);
			if (
				channels?.email?.messageTemplate === templateId ||
				channels?.sms?.messageTemplate === templateId
			) {
				usedBy.push({ name: profile.name, type: 'Digital Profile' });
			}
		} catch (error) {
			console.error(
				`Failed to parse channels for digital profile ${profile.id}`,
				error,
			);
		}
	}

	if (usedBy.length > 0) {
		return {
			inUse: true,
			message:
				'This message template is currently in use in a Digital Profile and cannot be deleted.',
			usedBy,
		};
	}

	return { inUse: false };
}

export async function checkPaymentProfileUsage(profileId, tenantId) {
	const tenantProfiles = await tenantGraphqlRequest(profileByTenant, tenantId);

	// Check if any of the tenant's digital channels are using the payment profile
	const usedBy = tenantProfiles.profileByTenant.items
		.filter(
			(profile) =>
				profile.channels &&
				profile.channels.some(
					(channel) => channel.paymentProfileId === profileId,
				),
		)
		.map((profile) => ({ name: profile.name, type: 'Communication Profile' }));

	if (usedBy.length > 0) {
		return {
			inUse: true,
			message:
				'This payment profile is currently in use in a Communication Profile and cannot be deleted.',
			usedBy,
		};
	}

	return { inUse: false };
}

export async function checkSuppressionProfileUsage(profileId, tenantId) {
	const tenantSegments = await tenantGraphqlRequest(segmentByTenant, tenantId);

	// Check if any of the tenant's segments are using the suppression profile
	const segmentsUsingProfile = tenantSegments.segmentByTenant.items
		.filter((segment) => segment.suppressionID === profileId)
		.map((segment) => ({ name: segment.name, type: 'Segment' }));

	if (segmentsUsingProfile.length > 0) {
		return {
			inUse: true,
			message:
				'This suppression profile is currently in use by a segment and cannot be deleted.',
			usedBy: segmentsUsingProfile,
		};
	}

	const manualProfiles = await tenantGraphqlRequest(
		manualProfileByTenant,
		tenantId,
	);
	const manualProfilesUsingProfile = manualProfiles.manualProfileByTenant.items
		.filter((profile) => profile.suppressionProfile === profileId)
		.map((profile) => ({ name: profile.name, type: 'Manual Dialing Profile' }));

	if (manualProfilesUsingProfile.length > 0) {
		return {
			inUse: true,
			message:
				'This suppression profile is currently in use by a manual dialing profile and cannot be deleted.',
			usedBy: manualProfilesUsingProfile,
		};
	}

	return { inUse: false };
}

export async function checkDNCListUsage(dncListId, tenantId) {
	const tenantSuppressionProfiles = await tenantGraphqlRequest(
		listSuppressions,
		tenantId,
	);

	// Check if any of the tenant's suppression profiles are using the DNC list
	const usedBy = tenantSuppressionProfiles.listSuppressions.items
		.filter(
			(profile) =>
				profile.checks &&
				profile.checks.some((check) =>
					check.dncListSuppressions?.dncs?.includes(dncListId),
				),
		)
		.map((profile) => ({ name: profile.name, type: 'Suppression Profile' }));

	if (usedBy.length > 0) {
		return {
			inUse: true,
			message:
				'This DNC list is currently in use by a suppression profile and cannot be deleted.',
			usedBy,
		};
	}

	return { inUse: false };
}

export async function checkContactFieldSuppresionListUsage(listId, tenantId) {
	const tenantSuppressionProfiles = await tenantGraphqlRequest(
		listSuppressions,
		tenantId,
	);

	// Check if any of the tenant's suppression profiles are using the contact field suppression list
	const usedBy = tenantSuppressionProfiles.listSuppressions.items
		.filter(
			(profile) =>
				profile.checks &&
				profile.checks.some(
					(check) =>
						check.contactFieldSuppressions?.contactFieldSuppressions &&
						check.contactFieldSuppressions.contactFieldSuppressions.some(
							(suppression) => JSON.parse(suppression).id === listId,
						),
				),
		)
		.map((profile) => ({ name: profile.name, type: 'Suppression Profile' }));

	if (usedBy.length > 0) {
		return {
			inUse: true,
			message:
				'This contact field suppression list is currently in use by a suppression profile and cannot be deleted.',
			usedBy,
		};
	}

	return { inUse: false };
}

/**
 * Determine whether a custom field is referenced by any segment. Segments store
 * their rules as JSON on `query.qbQuery` and `filter.qbQuery`; a rule that uses
 * a custom field has a `field` of the form `custom:<fieldName>`.
 */
export async function checkCustomFieldUsage(fieldName, tenantId) {
	const [segments, contactFieldSuppressionLists] = await Promise.all([
		getAllTenantSegments(tenantId),
		getAllContactFieldSuppressionLists(),
	]);

	const usedBy = [];

	// Segments reference custom fields in their query/filter rules as
	// `custom:<fieldName>`.
	for (const segment of segments) {
		if (segmentReferencesCustomField(segment, fieldName)) {
			usedBy.push({ name: segment.name, type: 'Segment' });
		}
	}

	// Contact field suppression lists reference a custom field via their
	// `contactField` value, also stored as `custom:<fieldName>`.
	const customFieldRef = `custom:${fieldName}`;
	for (const list of contactFieldSuppressionLists) {
		if (list.contactField === customFieldRef) {
			usedBy.push({ name: list.name, type: 'Contact Field Suppression List' });
		}
	}

	if (usedBy.length > 0) {
		return {
			inUse: true,
			message:
				'This custom field is currently in use and cannot be deleted. Remove the field from the items below before deleting it.',
			usedBy,
		};
	}

	return { inUse: false };
}

/** Helper Functions */

function segmentReferencesCustomField(segment, fieldName) {
	const ruleTrees = [segment?.query?.qbQuery, segment?.filter?.qbQuery];
	return ruleTrees.some((rawTree) => {
		if (!rawTree) {
			return false;
		}
		let tree = rawTree;
		if (typeof tree === 'string') {
			try {
				tree = JSON.parse(tree);
			} catch (error) {
				console.error(
					`Failed to parse qbQuery for segment ${segment.id}`,
					error,
				);
				return false;
			}
		}
		return getCustomFieldsFromRules(tree).includes(fieldName);
	});
}

// Recursively collect the custom field names referenced by a react-querybuilder
// rule tree. Mirrors getCustomFieldsFromRules in the segment editor.
function getCustomFieldsFromRules(rule) {
	if (!rule) {
		return [];
	}
	const customFields = [];
	if (rule.field) {
		if (typeof rule.field === 'string' && rule.field.startsWith('custom:')) {
			customFields.push(rule.field.split(':')[1]);
		}
	} else if (Array.isArray(rule.rules)) {
		for (const subRule of rule.rules) {
			customFields.push(...getCustomFieldsFromRules(subRule));
		}
	}
	return customFields;
}

// Fetch every campaign for the tenant regardless of status, paging through all
// results. Used by segment usage checks so Pending campaigns (including clones
// of completed campaigns) are not missed.
async function getAllTenantCampaigns(tenantId) {
	return getAllTenantItems(campaignsByTenant, 'campaignsByTenant', tenantId);
}

// Fetch every segment for the tenant, paging through all results. Used by the
// custom field usage check.
async function getAllTenantSegments(tenantId) {
	return getAllTenantItems(segmentByTenant, 'segmentByTenant', tenantId);
}

// Fetch every contact field suppression list, paging through all results. This
// is a plain list query (tenant scoping is applied by the resolver), so no
// tenant variable is passed, matching how the page itself queries it.
async function getAllContactFieldSuppressionLists() {
	return fetchAllPages({
		query: listContactFieldSuppressionLists,
		resultKey: 'listContactFieldSuppressionLists',
	});
}

// Convenience wrapper over the shared pager for the tenant "byTenant" queries.
async function getAllTenantItems(query, resultKey, tenantId) {
	return fetchAllPages({ query, resultKey, variables: { tenant: tenantId } });
}

async function tenantGraphqlRequest(query, tenantId) {
	const client = generateClient();
	const response = await client.graphql({
		query,
		variables: {
			tenant: tenantId,
		},
	});

	return response.data;
}
